/**
 * Which language the interface is in, and who decided.
 *
 * Zustand rather than TanStack Query because the *decision* is client state even though half
 * its input is not: the studio's tag arrives with the session (`features/session`), and this
 * store is where it meets the reader's own pre-auth choice and one of them wins.
 *
 * **Two inputs, one derived answer.** `chosen` is the pre-auth choice — the switcher on the
 * entrance screens, remembered in `localStorage` because nobody has signed in yet and there is
 * no profile to hang it on. `studioTag` is `workspace.locale` for the studio this tab is
 * acting as, held for the session only and never persisted: it belongs to the studio, not to
 * this browser, and a copy of it in `localStorage` would outlive both the session and the
 * setting it was copied from. `locale` is what every screen reads, and it is recomputed by
 * `effectiveLocale` whenever either input moves — which is what makes a save in Studio
 * settings land on the screen in the same commit, with no reload (`MCK-20260521-010`).
 *
 * **There is deliberately no personal override on top of the studio's choice.** Not an
 * omission: `user_account.ui_language` is declared, read on `/auth/me` and has no writer, and
 * the owner's 2026-09-12 decision leaves it that way. `setLocale` writes the *pre-auth* choice
 * and loses to a studio that has one; that is the whole hierarchy, and adding a second winner
 * would need a decision nobody has asked for.
 *
 * **The date shape is a third input, and it is not a language** (`MCK-20260521-010`, owner
 * 2026-09-15). `dateFormat` is this browser's choice between the automatic, numeric and long
 * short date, persisted in `localStorage` and read by `formats.ts` alone. `effectiveLocale`
 * never sees it and the studio does not decide it: a firm needs its screens to *read* the same
 * to everybody, not to draw a date the same way, so the rule above does not reach this field.
 *
 * **`locale` only ever names a language whose pack is here** (2026-09-25, when the packs
 * became `import()`s — `catalog.ts`). Both writers below resolve the new language, and if its
 * pack has not arrived they fetch it and commit the switch when it lands, keeping the old
 * language on screen meanwhile. The alternative — switch first, translate when the chunk
 * arrives — shows every key in English for the length of a network round trip, which is the
 * flash `RequireSession`'s layout effect exists to prevent. The initial value is exempt
 * because `app/main.tsx` awaits that pack before the first render.
 */

import { create } from 'zustand';

import { isPackReady, loadPack } from './catalog';
import type { DateFormatChoice, Locale } from './locales';
import {
  effectiveLocale,
  readStoredDateFormat,
  readStoredLocale,
  writeStoredDateFormat,
  writeStoredLocale,
} from './locales';

interface LocaleState {
  /** What the interface renders in: the studio's language, else the reader's own. */
  readonly locale: Locale;
  /** The pre-auth choice, persisted. Applies until a studio has one. */
  readonly chosen: Locale;
  /** `workspace.locale` for the active studio, or `null` when nobody has chosen. */
  readonly studioTag: string | null;
  readonly setLocale: (locale: Locale) => void;
  /**
   * Take the studio's language, or give it back.
   *
   * Called with the active studio's tag while signed in and with `null` otherwise — on the
   * entrance, on a workspace that has not been through B0, and on sign-out. Passing `null`
   * back is what returns the entrance screens to the reader's own choice rather than leaving
   * a departed studio's language on a sign-in form.
   */
  readonly adoptStudioLocale: (studioTag: string | null) => void;
  /** This browser's short-date shape, persisted. Independent of `locale`. */
  readonly dateFormat: DateFormatChoice;
  readonly setDateFormat: (choice: DateFormatChoice) => void;
}

export const useLocaleStore = create<LocaleState>((set, get) => {
  const chosen = readStoredLocale();

  /**
   * Commit a language once it can be drawn. Synchronous when the pack is already here — the
   * ordinary case, and the one `RequireSession`'s layout effect relies on — and otherwise after
   * the fetch, re-checking that the answer is still wanted: a person who switched twice before
   * the first chunk landed gets the second language, not whichever download finished last.
   */
  const settle = (locale: Locale, stillWanted: () => boolean): void => {
    if (isPackReady(locale)) {
      set((state) => (state.locale === locale ? state : { locale }));
      return;
    }
    void loadPack(locale)
      .catch(() => undefined)
      .then(() => {
        if (stillWanted()) set((state) => (state.locale === locale ? state : { locale }));
      });
  };

  return {
    locale: chosen,
    chosen,
    studioTag: null,
    dateFormat: readStoredDateFormat(),
    setDateFormat: (choice) => {
      writeStoredDateFormat(choice);
      set((state) => (state.dateFormat === choice ? state : { dateFormat: choice }));
    },
    setLocale: (locale) => {
      writeStoredLocale(locale);
      set({ chosen: locale });
      const wanted = effectiveLocale(get().studioTag, locale);
      settle(wanted, () => effectiveLocale(get().studioTag, get().chosen) === wanted);
    },
    adoptStudioLocale: (studioTag) => {
      // Same tag, same object: `set` with a fresh object would notify every subscriber on
      // every session refetch, which is a re-render of every screen in the app for a value
      // that did not move.
      if (get().studioTag === studioTag) return;
      set({ studioTag });
      const wanted = effectiveLocale(studioTag, get().chosen);
      settle(wanted, () => effectiveLocale(get().studioTag, get().chosen) === wanted);
    },
  };
});
